import { DataProvider, fetchUtils } from 'react-admin';
import { stringify } from 'query-string';
import { tenantService } from '../services/tenantService';

const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;

// Function to check if resource is a tenant-scoped resource
const isTenantScopedResource = (resource: string) => {
  return resource.includes('/users') || resource.includes('/tenant-users');
};

// Default resources (users, drivers, tasks, etc.)
export const defaultResources = [
  'users',
  'users/search-available',
  'tenant-users',
  'drivers',
  'driver-licenses',
  'driver-certifications',
  'driver-education',
  'driver-employment',
  'driver-vehicles',
  'license-types',
  'certification-types',
  'education-schools',
  'tasks',
  'task-categories',
  'task-statuses',
  'forms',
  'completed-forms',
];

// Helper function to build query parameters for default resources
const buildDefaultQueryParams = (params: any) => {
  const { page, perPage } = params.pagination || { page: 1, perPage: 25 };
  const { field, order } = params.sort || { field: 'id', order: 'DESC' };

  return {
    page: page,
    perPage: perPage,
    sort: field,
    order: order.toLowerCase(),
    ...params.filter,
  };
};

// HTTP client with authentication and tenant headers
const httpClient = async (url: string, options: any = {}) => {
  if (!options.headers) {
    options.headers = new Headers({ Accept: 'application/json' });
  }

  // Only set Content-Type if not using FormData
  if (
    !(options.body instanceof FormData) &&
    options.method !== 'GET' &&
    options.method !== 'DELETE'
  ) {
    options.headers.set('Content-Type', 'application/json');
  }

  const accessToken = localStorage.getItem('accessToken');
  if (accessToken) {
    options.headers.set('Authorization', `Bearer ${accessToken}`);
  }

  // Add tenant code header (similar to fortress-web approach)
  try {
    const tenantCode = tenantService.getCurrentTenantCode();
    options.headers.set('x-tenant-code', tenantCode);
  } catch (error) {
    throw new Error('No tenant selected. Please select a tenant to continue.');
  }

  try {
    const response = await fetchUtils.fetchJson(url, options);
    return response;
  } catch (error: any) {
    if (error.status === 401) {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    throw error;
  }
};

/** GET /completed-forms/reports — skip/perPage/orderBy/where (matches dv-web + ext-server). */
const getCompletedFormsReports = async (params: {
  pagination?: { page?: number; perPage?: number };
  sort?: { field?: string; order?: string };
  filter?: Record<string, unknown>;
}) => {
  const { page = 1, perPage = 25 } = params.pagination || {};
  const { field = 'completedAt', order = 'DESC' } = params.sort || {};
  const query: Record<string, string | number> = {
    skip: (page - 1) * perPage,
    perPage,
    orderBy: JSON.stringify({ [field]: String(order).toLowerCase() }),
  };
  if (params.filter && Object.keys(params.filter).length > 0) {
    query.where = JSON.stringify(params.filter);
  }
  const url = `${baseUrl}/completed-forms/reports?${stringify(query)}`;
  const { json, headers } = await httpClient(url);

  let data: unknown;
  let total: number;

  if (json.data && json.metadata) {
    data = json.data;
    total = json.metadata.total;
  } else if (Array.isArray(json)) {
    data = json;
    total = parseInt(headers.get('X-Total-Count') || json.length.toString(), 10);
  } else {
    data = json.data || json;
    total = json.total || parseInt(headers.get('X-Total-Count') || '0', 10);
  }

  return { data, total };
};

export const defaultDataProvider: DataProvider = {
  getList: async (resource, params) => {
    if (resource === 'completed-forms/reports') {
      try {
        return await getCompletedFormsReports(params);
      } catch (error) {
        console.error(`Error fetching ${resource} list:`, error);
        throw error;
      }
    }

    const query = buildDefaultQueryParams(params);
    const url = `${baseUrl}/${resource}?${stringify(query)}`;

    try {
      const { json, headers } = await httpClient(url);

      let data, total;

      if (json.data && json.metadata) {
        data = json.data;
        total = json.metadata.total;
      } else if (Array.isArray(json)) {
        data = json;
        total = parseInt(headers.get('X-Total-Count') || json.length.toString(), 10);
      } else {
        data = json.data || json;
        total = json.total || parseInt(headers.get('X-Total-Count') || '0', 10);
      }

      return { data, total };
    } catch (error) {
      console.error(`Error fetching ${resource} list:`, error);
      throw error;
    }
  },

  getOne: async (resource, params) => {
    const url = `${baseUrl}/${resource}/${params.id}`;

    try {
      const { json } = await httpClient(url);

      // Special handling for tenants: map code to id
      if (resource === 'tenants' && json && json.code && !json.id) {
        json.id = json.code;
      }

      return { data: json };
    } catch (error) {
      console.error(`Error fetching ${resource}/${params.id}:`, error);
      throw error;
    }
  },

  getMany: async (resource, params) => {
    if (!params.ids?.length) {
      return { data: [] };
    }

    const ids = params.ids.map((id) =>
      typeof id === 'string' && /^\d+$/.test(id) ? Number(id) : id,
    );
    const query = {
      page: 1,
      perPage: Math.min(1000, Math.max(25, ids.length * 2 || 25)),
      sort: 'id',
      order: 'asc',
      where: JSON.stringify({ id: { in: ids } }),
    };
    const url = `${baseUrl}/${resource}?${stringify(query)}`;

    try {
      const { json } = await httpClient(url);

      let data: unknown;
      if (json?.data && json?.metadata) {
        data = json.data;
      } else if (Array.isArray(json)) {
        data = json;
      } else {
        data = json?.data ?? [];
      }

      if (!Array.isArray(data)) {
        console.error(
          `getMany(${resource}): expected array in response, got`,
          typeof data,
          json,
        );
        return { data: [] };
      }

      return { data };
    } catch (error) {
      console.error(`Error fetching multiple ${resource}:`, error);
      return { data: [] };
    }
  },

  getManyReference: async (resource, params) => {
    if (resource === 'completed-forms/reports') {
      const filter = { ...(params.filter || {}) };
      if (params.target === 'userId') {
        filter.userId = params.id;
      }
      try {
        return await getCompletedFormsReports({
          pagination: params.pagination,
          sort: params.sort,
          filter,
        });
      } catch (error) {
        console.error(`Error fetching ${resource} references:`, error);
        return { data: [], total: 0 };
      }
    }

    const query = buildDefaultQueryParams(params);
    query[params.target] = params.id;
    const url = `${baseUrl}/${resource}?${stringify(query)}`;

    try {
      const { json, headers } = await httpClient(url);

      let data, total;

      if (json.data && json.metadata) {
        data = json.data;
        total = json.metadata.total;
      } else if (Array.isArray(json)) {
        data = json;
        total = parseInt(headers.get('X-Total-Count') || json.length.toString(), 10);
      } else {
        data = json.data || json;
        total = json.total || parseInt(headers.get('X-Total-Count') || '0', 10);
      }

      return { data, total };
    } catch (error) {
      console.error(`Error fetching ${resource} references:`, error);
      return { data: [], total: 0 };
    }
  },

  create: async (resource, params) => {
    const url = `${baseUrl}/${resource}`;

    try {
      let body;
      // Handle file uploads
      if (resource === 'files' && params.data.file) {
        const formData = new FormData();
        formData.append('file', params.data.file);
        formData.append('name', params.data.name || params.data.file.name);
        formData.append('folder', params.data.folder || '');
        formData.append('description', params.data.description || '');
        formData.append('isPublic', params.data.isPublic?.toString() || 'false');
        body = formData;
      } else {
        body = JSON.stringify(params.data);
      }

      const { json } = await httpClient(url, {
        method: 'POST',
        body: body,
      });

      // Special handling for tenants: map code to id
      if (resource === 'tenants' && json && json.code && !json.id) {
        json.id = json.code;
      }

      return {
        data: { ...params.data, ...json },
      };
    } catch (error) {
      console.error(`Error creating ${resource}:`, error);
      throw error;
    }
  },

  update: async (resource, params) => {
    const url = `${baseUrl}/${resource}/${params.id}`;

    try {
      const { json } = await httpClient(url, {
        method: 'PATCH',
        body: JSON.stringify(params.data),
      });

      // Special handling for tenants: map code to id
      if (resource === 'tenants' && json && json.code && !json.id) {
        json.id = json.code;
      }

      return { data: json };
    } catch (error) {
      console.error(`Error updating ${resource}/${params.id}:`, error);
      throw error;
    }
  },

  updateMany: async (resource, params) => {
    try {
      await Promise.all(
        params.ids.map(id =>
          httpClient(`${baseUrl}/${resource}/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(params.data),
          })
        )
      );

      return { data: params.ids };
    } catch (error) {
      console.error(`Error updating multiple ${resource}:`, error);
      throw error;
    }
  },

  delete: async (resource, params) => {
    const url = `${baseUrl}/${resource}/${params.id}`;

    try {
      const { json } = await httpClient(url, {
        method: 'DELETE',
      });

      return { data: json };
    } catch (error) {
      console.error(`Error deleting ${resource}/${params.id}:`, error);
      throw error;
    }
  },

  deleteMany: async (resource, params) => {
    try {
      await Promise.all(
        params.ids.map(id =>
          httpClient(`${baseUrl}/${resource}/${id}`, {
            method: 'DELETE',
          })
        )
      );

      return { data: params.ids };
    } catch (error) {
      console.error(`Error deleting multiple ${resource}:`, error);
      throw error;
    }
  },
};
